import React, { ChangeEvent, FormEvent, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { cloneDeep } from 'lodash-es';
import { Form } from 'pages/AuthPage/AuthPage.styles';
import {
  validConfirmPassword,
  validPassword,
} from 'utils/helpers/formValidators';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordTextField from 'atoms/PasswordTextField/PasswordTextField';
import { PASSWORD_CONFIRM_ERROR_MESSAGE } from 'utils/constants/form';
import Link from 'atoms/Link/Link';
import { palette } from 'utils/styles/variables';
import { useWindowDimensions } from 'pages/AuthPage/AuthPage';

const ChangePasswordForm = () => {
  const { width } = useWindowDimensions();
  const [form, setForm] = useState({
    password: { value: '', error: '' },
    confirmPassword: { value: '', error: '' },
  });
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('access_token');

  if (!accessToken) {
    return (
      <Alert severity="error">
        Invalid or expired link. Please request a new password reset.
      </Alert>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const updatedForm = cloneDeep(form);
    if (
      !validConfirmPassword(form.password.value, form.confirmPassword.value)
    ) {
      updatedForm.confirmPassword.error = PASSWORD_CONFIRM_ERROR_MESSAGE;
    }
    if (
      Boolean(updatedForm.password.error) ||
      Boolean(updatedForm.confirmPassword.error)
    ) {
      return setForm(updatedForm);
    }
    try {
      await axios.patch(
        `${process.env.REACT_APP_USER_SERVICE}/update-password`,
        {
          password: form.password.value,
        },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      return navigate('/signin');
    } catch (err) {
      if (err.response?.status === 401) {
        return setApiError(
          'Password reset link has expired. Please request it again'
        );
      }
      return setApiError(err.response?.data?.message || 'An error occurred');
    }
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    let { error } = form.password;
    if (validPassword(newPassword)) {
      error = '';
    }
    if (validConfirmPassword(newPassword, form.confirmPassword.value)) {
      form.confirmPassword.error = '';
    }
    setForm((prevState) => ({
      ...prevState,
      password: { ...prevState.password, value: newPassword, error },
    }));
  };

  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    let { error } = form.confirmPassword;
    if (validConfirmPassword(form.password.value, newConfirmPassword)) {
      error = '';
    }
    setForm((prevState) => ({
      ...prevState,
      confirmPassword: {
        ...prevState.confirmPassword,
        value: newConfirmPassword,
        error,
      },
    }));
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {apiError && (
        <Alert severity="error">{apiError.replaceAll('_', ' ')}</Alert>
      )}
      <PasswordTextField
        autoComplete="new-password"
        fullWidth
        variant="outlined"
        label="Password *"
        margin="normal"
        error={!!form.password.error}
        helperText={form.password.error}
        onChange={handlePasswordChange}
        InputLabelProps={{ shrink: true }}
      />
      <PasswordTextField
        autoComplete="new-password"
        fullWidth
        variant="outlined"
        label="Confirm Password *"
        margin="normal"
        error={!!form.confirmPassword.error}
        helperText={form.confirmPassword.error}
        onChange={handleConfirmPasswordChange}
        InputLabelProps={{ shrink: true }}
      />
      <Button
        fullWidth
        variant="contained"
        type="submit"
        sx={{
          mt: '24px',
          lineHeight: '20px',
          textTransform: 'uppercase',
          borderRadius: '3px',
        }}
      >
        Change password
      </Button>

      {width < 600 ? (
        <Typography
          textAlign="right"
          variant="body1"
          fontSize="0.875rem"
          color="#999999"
          className="form-footer"
        >
          Back to{' '}
          <Link to="/signin" $color={palette.primary.main}>
            Sign in
          </Link>
        </Typography>
      ) : (
        ''
      )}
    </Form>
  );
};

export default ChangePasswordForm;
